import { useState } from 'react';

import {
  CampaignSettings,
  createCampaignSettings,
  useListCampaignsLive,
  useShowDonationItem,
} from '@givebacks/api/store';
import { useShowCause } from 'core/Api';
import { CauseCard } from 'core/Components';
import { useBreakPoints, useCrowdFundingTranslations } from 'core/Hooks';
import { Box, Grid, Stack, Text } from 'ui';

import { CampaignCard } from '../../../../crowdfunding/components/Cards/CampaignCard/CampaignCard';

type SelectProps = {
  storeId?: string;
  storeItemId?: string;
};
type CampaignSelectionProps = {
  onPeerCreditOpen: ({ storeId, storeItemId }: SelectProps) => void;
  onItemSelect: ({ storeId, storeItemId }: SelectProps) => void;
  storeId?: string;
};

type OnCampaignSelect = ({
  campaignId,
  campaignSettings,
}: {
  campaignId: string | undefined;
  campaignSettings?: CampaignSettings;
}) => void;
type CampaignsProps = {
  causeId?: string;
  onSelect: OnCampaignSelect;
  selectedId?: string;
  hasBeta?: boolean;
};
const Campaigns = ({ causeId, onSelect, selectedId, hasBeta }: CampaignsProps) => {
  const { t } = useCrowdFundingTranslations(causeId);
  const { isSmallScreen } = useBreakPoints();

  const { data: campaigns } = useListCampaignsLive({ causeId });

  if (!hasBeta) return null;
  if (Boolean(campaigns?.length) == false) return null;
  return (
    <Stack>
      <Text fz="lg" fw={600}>
        Campaign {t('Donation')}
      </Text>
      <Grid mih={212} gutter={'lg'}>
        {campaigns?.map((campaign) => {
          return (
            <Grid.Col span={{ xs: 12, sm: 4 }} key={campaign.uuid} mih="100%">
              <CampaignCard
                campaign_id={campaign.uuid}
                onClick={() => {
                  onSelect({
                    campaignId: campaign.uuid,
                    campaignSettings: createCampaignSettings(campaign),
                  });
                }}
                causeId={causeId}
                isColumn={!isSmallScreen}
              />
            </Grid.Col>
          );
        })}
      </Grid>
    </Stack>
  );
};

const CampaignSelection = ({ onPeerCreditOpen, onItemSelect, storeId }: CampaignSelectionProps) => {
  const [selected, setSelected] = useState<string | undefined>();
  const { data: cause } = useShowCause(storeId);
  const { data: donationItem } = useShowDonationItem({ storeId });
  const { t } = useCrowdFundingTranslations(storeId);

  const handleDonate: OnCampaignSelect = ({ campaignId, campaignSettings }) => {
    if (campaignId) {
      setSelected(campaignId);
      if (campaignSettings?.isPeersEnabled || campaignSettings?.isTeamsEnabled) {
        onPeerCreditOpen({ storeId, storeItemId: campaignId });
      } else {
        onItemSelect({ storeId, storeItemId: campaignId });
      }
    } else if (storeId) {
      onItemSelect({ storeId, storeItemId: donationItem?.uuid });
    }
  };

  if (!storeId) return null;
  return (
    <Stack maw="100%" gap="lg">
      <Text fz="lg" fw={600}>
        General {t('Donation')}
      </Text>
      <Box
        component="button"
        onClick={() => handleDonate({ campaignId: undefined })}
        style={(theme) => ({
          borderRadius: theme.radius.md,
        })}
      >
        <CauseCard cause_uuid={storeId} />
      </Box>
      <Campaigns
        causeId={storeId}
        selectedId={selected}
        onSelect={handleDonate}
        hasBeta={cause?.release.campaigns.beta}
      />
    </Stack>
  );
};

export { CampaignSelection };
